<script setup lang="ts">
import { ref, toRefs } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';

import { requiredRule, noAmpersandRegexRule } from '@/helpers/FormRules';
import { type VisionProject, VisionProjectType } from '@/models/RobVision';
import { projectTypeLabel } from '@/helpers/RobvisionHelper';
import gettext from '@/i18n/gettext';

const props = defineProps<{
  isOpen: boolean;
  existingProjects: VisionProject[];
}>();
const { isOpen } = toRefs(props);
const valid = ref(false);
const emit = defineEmits<{
  (e: 'onTypeSelected', name: string, type: VisionProjectType): void;
  (e: 'update:isOpen', value: boolean): void;
}>();

const selectedType = ref<VisionProjectType | null>(null);
const projectName = ref('');
const { $gettext } = gettext;

// One source of truth for type names (see projectTypeLabel); only the
// "… Project" suffix is composed here.
const typeRadioLabel = (type: VisionProjectType) =>
  $gettext('%{ type } Project', { type: projectTypeLabel(type) });

const resetAndClose = () => {
  // Reset form
  emit('update:isOpen', false);
  setTimeout(() => {
    selectedType.value = null;
    projectName.value = '';
  }, 50);
};

const createProject = async () => {
  if (selectedType.value && projectName.value.trim()) {
    emit('onTypeSelected', projectName.value, selectedType.value);
    resetAndClose();
  }
};
</script>

<template>
  <v-dialog
    v-model="isOpen"
    class="vision-project-dialog"
    :max-width="600"
    @update:model-value="resetAndClose"
  >
    <v-card>
      <v-card-title>{{ $gettext('Add Vision Project') }}</v-card-title>
      <v-card-text>
        <v-form v-model="valid" @submit.prevent>
          <v-text-field
            v-model="projectName"
            :autofocus="true"
            class="v-label v-label--clickable"
            :label="$gettext('Project Name')"
            :rules="[
              requiredRule,
              noAmpersandRegexRule,
              (v: string) =>
                !existingProjects
                  .map((project: VisionProject) => project.name)
                  .includes(v) || $gettext('Project name already exists'),
            ]"
          />
          <div class="mb-2">
            {{
              $gettext(
                'Please select the type of vision project you want to create. You can train a picking model to pick objects with a RobVision equipped Robot. CAD Picking enables you to train a picking model based on a CAD model. With Label Picking you can label the object on a image of the part. Depalletizing works like label picking with a focus on depalletizing.',
              )
            }}
          </div>
          <v-radio-group v-model="selectedType" :rules="[requiredRule]">
            <v-radio
              :value="VisionProjectType.CADPicking"
              :label="typeRadioLabel(VisionProjectType.CADPicking)"
            />
            <v-radio
              :value="VisionProjectType.LabelPicking"
              :label="typeRadioLabel(VisionProjectType.LabelPicking)"
            />
            <v-radio
              :value="VisionProjectType.Depalletizing"
              :label="typeRadioLabel(VisionProjectType.Depalletizing)"
            />
          </v-radio-group>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <r-btn variant="outlined" @click="resetAndClose">
          {{ $gettext('Cancel') }}
        </r-btn>
        <v-spacer />
        <r-btn
          variant="accent"
          icon="mdi-plus"
          :disabled="!valid"
          @click="createProject"
        >
          {{ $gettext('Create Vision Project') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss">
.vision-project-dialog {
  .v-label {
    opacity: 1 !important;
  }
}
</style>
